import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Shield, Lock, Database, Activity, CheckCircle2, FileText,
  Download, ChevronRight, ChevronDown, ExternalLink, Users, Eye, Server,
  Key, AlertTriangle, Globe, Clock, ArrowLeft
} from 'lucide-react';
import SEO from '@/components/SEO';
import DataFlowDiagram from '@/components/security/DataFlowDiagram';

const API = process.env.REACT_APP_BACKEND_URL;

// Feature icons mapping
const FEATURE_ICONS = {
  'Authentication': Lock,
  'Data Protection': Shield,
  'Audit & Compliance': Activity,
  'Infrastructure': Server
};

export default function SecurityPage() {
  const [policies, setPolicies] = useState([]);
  const [complianceSummary, setComplianceSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPolicy, setExpandedPolicy] = useState(null);
  const [policyContent, setPolicyContent] = useState({});
  const [expandedISO, setExpandedISO] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [policiesRes, complianceRes] = await Promise.all([
        fetch(`${API}/api/legal/policies`),
        fetch(`${API}/api/compliance/public-summary`)  // Use synced compliance data
      ]);

      if (policiesRes.ok) setPolicies(await policiesRes.json());
      if (complianceRes.ok) setComplianceSummary(await complianceRes.json());
    } catch (error) {
      console.error('Failed to load security data:', error);
    }
    setLoading(false);
  };

  const loadPolicyContent = async (policyId) => {
    if (policyContent[policyId]) {
      setExpandedPolicy(expandedPolicy === policyId ? null : policyId);
      return;
    }

    try {
      const response = await fetch(`${API}/api/legal/policies/${policyId}`);
      if (response.ok) {
        const data = await response.json();
        setPolicyContent(prev => ({ ...prev, [policyId]: data }));
        setExpandedPolicy(policyId);
      }
    } catch (error) {
      console.error('Failed to load policy:', error);
    }
  };

  const downloadPDF = (policyId) => {
    window.open(`${API}/api/legal/policies/${policyId}/pdf`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F5] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F5]">
      <SEO page="security" />
      {/* Navigation */}
      <nav className="bg-white border-b border-[#E6E0D9] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#4A3B32] flex items-center justify-center">
                <span className="text-white font-bold text-lg">Q</span>
              </div>
              <span className="font-heading text-xl text-[#2C2420]">QIndex One</span>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-sm text-[#5D524D] hover:text-[#2C2420] transition-colors">Home</Link>
              <Link to="/contact" className="text-sm text-[#5D524D] hover:text-[#2C2420] transition-colors">Contact</Link>
              <Link to="/login">
                <Button className="bg-[#4A3B32] hover:bg-[#3A2B22] text-white">Sign In</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#4A3B32] to-[#3A2B22] text-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-2 text-[#D4CEC8] mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span>Security & Compliance</span>
          </div>
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-4xl mb-4">Security & Compliance</h1>
              <p className="text-xl text-[#D4CEC8] max-w-2xl">
                QIndex One is built with enterprise-grade security. We protect your client data
                with controls aligned to ISO 27001:2022 and Australian privacy regulations, and
                we are working toward formal SOC 2 Type 1 attestation (target Q3 2026).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Overview */}
      {complianceSummary && (
        <section className="py-16 bg-white border-b border-[#E6E0D9]">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="font-heading text-2xl text-[#2C2420] mb-8">Compliance Status</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {complianceSummary.compliance_frameworks.map((framework, idx) => (
                <div 
                  key={idx}
                  className="p-5 rounded-xl border border-[#E6E0D9] bg-[#FAFAF9] flex items-start gap-4"
                >
                  {framework.status === 'compliant' ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                  ) : framework.status === 'aligned' ? (
                    <Shield className="w-6 h-6 text-blue-500 flex-shrink-0" />
                  ) : (
                    <Clock className="w-6 h-6 text-amber-500 flex-shrink-0" />
                  )}
                  <div>
                    <h3 className="font-medium text-[#2C2420]">{framework.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      framework.status === 'compliant' 
                        ? 'bg-emerald-100 text-emerald-700'
                        : framework.status === 'aligned'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {framework.status === 'compliant' ? 'Compliant' : 
                       framework.status === 'aligned' ? 'Aligned' : 'In Progress'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* ISO 27001 Progress */}
            {complianceSummary.certifications.map((cert, idx) => (
              <div key={idx} className="rounded-xl border border-[#E6E0D9] bg-gradient-to-r from-[#F9F7F5] to-white overflow-hidden">
                <button 
                  onClick={() => setExpandedISO(!expandedISO)}
                  className="w-full p-6 flex items-center justify-between hover:bg-[#F5F3F1] transition-colors"
                  data-testid="iso-expand-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#4A3B32] flex items-center justify-center">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-heading text-lg text-[#2C2420]">{cert.name}</h3>
                      <p className="text-sm text-[#5D524D]">{cert.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className="text-2xl font-bold text-[#4A3B32]">
                        {Math.round((cert.controls_implemented / cert.total_controls) * 100)}%
                      </span>
                      <p className="text-xs text-[#8C8078]">
                        {cert.controls_implemented}/{cert.total_controls} controls
                      </p>
                    </div>
                    {expandedISO ? (
                      <ChevronDown className="w-5 h-5 text-[#8C8078]" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-[#8C8078]" />
                    )}
                  </div>
                </button>
                
                {/* Progress Bar */}
                <div className="px-6 pb-4">
                  <Progress value={(cert.controls_implemented / cert.total_controls) * 100} className="h-2" />
                </div>

                {/* Expanded Controls List */}
                {expandedISO && (
                  <div className="border-t border-[#E6E0D9] bg-white">
                    {/* Implemented Controls */}
                    {cert.implemented_controls && cert.implemented_controls.length > 0 && (
                      <div className="p-6 border-b border-[#E6E0D9]">
                        <h4 className="text-sm font-medium text-emerald-700 mb-4 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Implemented Controls ({cert.implemented_controls.length})
                        </h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {cert.implemented_controls.map((control, cidx) => (
                            <div 
                              key={cidx}
                              className="p-3 rounded-lg bg-emerald-50 border border-emerald-100"
                            >
                              <div className="flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-[#2C2420]">{control.id}</p>
                                  <p className="text-xs text-[#5D524D]">{control.name}</p>
                                  <p className="text-xs text-emerald-600 mt-1">{control.evidence}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Partial Controls */}
                    {cert.partial_controls && cert.partial_controls.length > 0 && (
                      <div className="p-6">
                        <h4 className="text-sm font-medium text-amber-700 mb-4 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Partially Implemented ({cert.partial_controls.length})
                        </h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {cert.partial_controls.map((control, cidx) => (
                            <div 
                              key={cidx}
                              className="p-3 rounded-lg bg-amber-50 border border-amber-100"
                            >
                              <div className="flex items-start gap-2">
                                <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-[#2C2420]">{control.id}</p>
                                  <p className="text-xs text-[#5D524D]">{control.name}</p>
                                  <p className="text-xs text-amber-600 mt-1">{control.evidence}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Security Features */}
      {complianceSummary && (
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="font-heading text-2xl text-[#2C2420] mb-8">Security Features</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {complianceSummary.security_features.map((category, idx) => {
                const Icon = FEATURE_ICONS[category.category] || Shield;
                return (
                  <div 
                    key={idx}
                    className="p-6 rounded-xl border border-[#E6E0D9] bg-white"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-[#4A3B32]/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#4A3B32]" />
                      </div>
                      <h3 className="font-heading text-lg text-[#2C2420]">{category.category}</h3>
                    </div>
                    <ul className="space-y-2">
                      {category.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-[#5D524D]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Policies Section */}
      <section className="py-16 bg-white border-t border-[#E6E0D9]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-2xl text-[#2C2420] mb-2">Legal & Policy Documents</h2>
          <p className="text-[#5D524D] mb-8">
            Read our policies or download them as PDF documents for your records.
          </p>
          
          <div className="space-y-4">
            {policies.map((policy) => (
              <div 
                key={policy.id}
                className="rounded-xl border border-[#E6E0D9] overflow-hidden bg-[#FAFAF9]"
              >
                {/* Policy Header */}
                <div className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#4A3B32]/10 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#4A3B32]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#2C2420]">{policy.title}</h3>
                      <p className="text-xs text-[#8C8078]">
                        Version {policy.version} • Effective {policy.effective_date}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadPolicyContent(policy.id)}
                      className="border-[#D4CEC8]"
                      data-testid={`view-policy-${policy.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      {expandedPolicy === policy.id ? 'Hide' : 'View'}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => downloadPDF(policy.id)}
                      className="bg-[#4A3B32] hover:bg-[#3A2B22]"
                      data-testid={`download-policy-${policy.id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                </div>

                {/* Policy Content (Expanded) */}
                {expandedPolicy === policy.id && policyContent[policy.id] && (
                  <div className="border-t border-[#E6E0D9] bg-white p-6 max-h-[600px] overflow-y-auto">
                    <div className="prose prose-sm max-w-none">
                      {policyContent[policy.id].sections.map((section, idx) => (
                        <div key={idx} className="mb-8">
                          <h4 className="font-heading text-lg text-[#2C2420] mb-3">{section.heading}</h4>
                          <div className="text-[#5D524D] whitespace-pre-wrap text-sm leading-relaxed">
                            {section.content.split('\n').map((para, pidx) => {
                              // Handle bold text
                              if (para.startsWith('**') && para.endsWith('**')) {
                                return <p key={pidx} className="font-semibold text-[#2C2420] mt-4 mb-2">{para.replace(/\*\*/g, '')}</p>;
                              }
                              // Handle bullet points
                              if (para.startsWith('- ') || para.startsWith('• ')) {
                                return <p key={pidx} className="ml-4">{para}</p>;
                              }
                              // Handle tables (simple markdown)
                              if (para.startsWith('|')) {
                                return <p key={pidx} className="font-mono text-xs bg-[#F5F3F1] p-1 rounded">{para}</p>;
                              }
                              return para ? <p key={pidx} className="mb-2">{para}</p> : null;
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <DataFlowDiagram />

      <section className="py-16 bg-[#4A3B32] text-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl mb-4">Your Data Security is Our Priority</h2>
          <p className="text-[#D4CEC8] max-w-2xl mx-auto mb-8">
            QIndex One is trusted by family offices and financial advisers across Australia. 
            We're committed to maintaining the highest standards of security and compliance.
          </p>
          <div className="flex items-center justify-center gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold">99.9%</div>
              <div className="text-sm text-[#D4CEC8]">Uptime SLA</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div className="text-center">
              <div className="text-3xl font-bold">AES-256</div>
              <div className="text-sm text-[#D4CEC8]">Encryption</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div className="text-center">
              <div className="text-3xl font-bold">7 Years</div>
              <div className="text-sm text-[#D4CEC8]">Audit Retention</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div className="text-center" data-testid="trust-stat-soc2">
              <div className="text-3xl font-bold">SOC 2</div>
              <div className="text-sm text-[#D4CEC8]">Type 1 in progress</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-gradient-to-r from-[#F9F7F5] to-white rounded-2xl p-8 border border-[#E6E0D9]">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-heading text-2xl text-[#2C2420] mb-2">Questions About Our Security?</h2>
                <p className="text-[#5D524D] mb-6">
                  Our team is happy to discuss our security practices and compliance posture.
                </p>
                <div className="flex gap-4">
                  <Link to="/contact">
                    <Button className="bg-[#4A3B32] hover:bg-[#3A2B22]">
                      Contact Us
                    </Button>
                  </Link>
                  <a href="mailto:security@qindexone.com">
                    <Button variant="outline" className="border-[#D4CEC8]">
                      security@qindexone.com
                    </Button>
                  </a>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="w-24 h-24 rounded-2xl bg-[#4A3B32]/10 flex items-center justify-center">
                  <Shield className="w-12 h-12 text-[#4A3B32]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C2420] text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                <span className="text-white font-bold text-lg">Q</span>
              </div>
              <span className="font-heading text-lg">QIndex One</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-[#8C8078]">
              <Link to="/security" className="hover:text-white transition-colors">Security</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <span>© 2026 QIndex One Pty Ltd</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
